import { store } from '../state/store';
import { addFiles, clearFiles, removeFiles } from '../state/slices/UploadFiles';
import type { DirectoryDestination, FileAttachment } from '../models/Document';
import { FileType } from '../models/Document';
import AuthService from './auth/AuthService';
import { PostMethod } from './constants';
import type { FileToUpload, FileToUploadAttribute } from '../models/Upload';
import { UploadFileLocation } from '../models/Upload';
import DocumentService, { RootFolderName } from './documents/DocumentService';
import DistributionService from './distribution/DistributionService';
import type { UploadFileForm } from '../components/fileUpload/UploadFlow/UploadFlow';
import type { AuthUser } from './auth/constants';
import UtilityService from './UtilityService';
import type { FileTypes, PageMenus } from '../models/FileTypes';
import type { WSResponse } from '../models/WSResponse';
import ScriptService from './ScriptService';
import NotificationUtility from './notifications/NotificationUtility';
import { NotificationStatus, NotificationType, UploadStatus } from '../models/Notifications';
import type { UseFormGetValues } from 'react-hook-form';
import moment from 'moment';

const { dispatch } = store;

export const DefaultFileCategory: string = 'other';
const BucketId: string = '8810931_';
export const GoogleUploadUrl: string = 'https://storage.googleapis.com/';

interface IUploadService {
  addUploadedFile: (file: FileAttachment) => void
  removeUploadedFile: (fileId: string) => void
  clearUploadedFiles: () => void
  addMultipleFiles: (files: FileAttachment[]) => void
  processFileUpload: (
    file: File, fileName: string, fileId: string,
    selectedDestinations: DirectoryDestination[], getValues: UseFormGetValues<UploadFileForm>,
    fileType: FileTypes, uploadDestination?: UploadFileLocation,
  ) => Promise<void>
  callUploadServlet: (fileId: string, file: File) => Promise<void>
  saveFileEntity: (
    file: FileAttachment, bucketName: string, uploadedFileURL: string,
    parents: string[], getValues: UseFormGetValues<UploadFileForm>,
    fileType: FileTypes, source: string, uploadDestination?: UploadFileLocation
  ) => Promise<void>
  callImportServlet: (files: File[], listIds: string[], departmentId: string, departmentName: string) => Promise<void>
  getBucketName: () => string
  getDefaultRootDestination: (fileId: string, folderId?: string) => DirectoryDestination[]
  getFileAttachmentsFromUploadedFilesJSON: (uploadedFilesJSON: string) => FileAttachment[]
}

class UploadService implements IUploadService {
  addUploadedFile (file: FileAttachment): void {
    dispatch(addFiles({ files: [file] }));
  }

  removeUploadedFile (fileId: string): void {
    dispatch(removeFiles({ files: [fileId] }))
  }

  clearUploadedFiles (): void {
    dispatch(clearFiles({}));
  }

  addMultipleFiles (files: FileAttachment[]): void {
    dispatch(addFiles({ files }));
  }

  async callUploadServlet (fileId: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadProgress = (progress: number): void => {
      NotificationUtility.updateUploadBox({
        fileId,
        progress
      })
    }

    await AuthService.authenticatedCall('/UploadServletV2', {
      method: PostMethod,
      body: formData,
      type: 'multipart/form-data',
      params: {
        fileName: fileId
      }
    },
    uploadProgress)
      .then(() => {})
      .catch((err) => {
        console.error(err);
      })
  }

  async processFileUpload (
    file: File, fileName: string, fileId: string,
    selectedDestinations: DirectoryDestination[], getValues: UseFormGetValues<UploadFileForm>,
    fileType: FileTypes, uploadDestination?: UploadFileLocation
  ): Promise<void> {
    const bucketName: string = this.getBucketName();
    const uploadedFileURL: string = `${GoogleUploadUrl}${bucketName}/${fileId}`;
    const parents: string[] = selectedDestinations.map((destination: DirectoryDestination) => destination.id);
    const fileAttachment: FileAttachment = {
      id: fileId,
      fileName,
      url: uploadedFileURL,
      isWatermarked: (getValues('watermark')) ? '1' : '0',
      size: file.size,
      subcategory: 'other',
      isPublishedToStudio: false,
      cloudProvider: '',
      fileExtension: (file.type === FileType.PDF) ? FileType.PDF : FileType.Other,
      type: 'file'
    }
    NotificationUtility.addUploadBox(fileId, fileName);
    NotificationUtility.addNotification(fileId, fileName, moment().valueOf(), NotificationType.Upload, true);
    await this.callUploadServlet(fileId, file);
    await this.saveFileEntity(fileAttachment, bucketName, uploadedFileURL, parents, getValues, fileType, 'local_system', uploadDestination);
  }

  async saveFileEntity (
    file: FileAttachment, bucketName: string, uploadedFileURL: string,
    parents: string[], getValues: UseFormGetValues<UploadFileForm>,
    fileType: FileTypes, source: string, uploadDestination?: UploadFileLocation
  ): Promise<void> {
    const authState = store.getState().auth;
    const crooglooAuth: AuthUser = authState.crooglooauth;
    const fileExtension: string = UtilityService.getFileExtension(file.fileName);
    let wmFirstLine: string = getValues('wmFirstLine') ?? '';
    if (wmFirstLine === 'Other') {
      wmFirstLine = 'Other~' + getValues('wmFirstOtherField');
    }
    let wmSecondLine: string = getValues('wmSecondLine') ?? '';
    if (wmSecondLine === 'Other') {
      wmSecondLine = 'Other~' + getValues('wmSecondOtherField');
    }
    const secListsWithAccess: string[] = getValues('shared');
    if (crooglooAuth.securityListId && !secListsWithAccess.includes(crooglooAuth.securityListId.trim().toUpperCase())) {
      secListsWithAccess.push(crooglooAuth.securityListId.trim().toUpperCase());
    }
    const documentCategory: string = 'DOCUMENT';
    const newFileAttributes: FileToUploadAttribute[] = [
      { id: 'id', value: file.id },
      { id: 'category', value: documentCategory },
      { id: 'limitToType', value: 'securityList' },
      { id: 'limitTo', value: secListsWithAccess.join(',') },
      { id: 'isWatermarked', value: getValues('watermark') ? 1 : 0 },
      { id: 'firstWatermarkLine', value: wmFirstLine },
      { id: 'secondWatermarkLine', value: wmSecondLine },
      { id: 'watermarkWithEncryption', value: getValues('wmWithEncryption') },
      { id: 'episode', value: getValues('episode') },
      { id: 'color', value: getValues('color') },
      { id: 'description' },
      { id: 'fileExtension', value: fileExtension },
      { id: 'bucket', value: bucketName },
      { id: 'showInReports', value: '0' },
      { id: 'submittedTo', value: '' },
      { id: 'fileURL', value: uploadedFileURL },
      { id: 'image', value: '' },
      { id: 'approval', value: '' },
      { id: 'approvedDate', value: '' },
      { id: 'approvedBy', value: '' },
      { id: 'showInHub', value: '0' },
      { id: 'title', value: file.fileName },
      { id: 'isSubmitted', value: '0' },
      { id: 'size', value: file.size },
      { id: 'priority', value: '0' },
      { id: 'subtitle', value: file.fileName },
      { id: 'submittedDate', value: '' },
      { id: 'fileName', value: file.fileName },
      { id: 'docDescription', value: '' },
      { id: 'submittedBy', value: '' },
      { id: 'parentFolder', value: (parents.length > 0) ? parents[0] : RootFolderName },
      { id: 'parents', value: parents.join(',') },
      { id: 'children', value: '' },
      { id: 'uploader_token', value: authState.userToken },
      { id: 'timeCreated', value: moment().toISOString() },
      { id: 'subcategory', value: file.subcategory },
      { id: 'lastModifBaseTime', value: moment().valueOf() },
      { id: 'source', value: source }
    ];

    const newFile: FileToUpload = {
      _ID: file.id,
      _NAME: file.fileName,
      entityType: documentCategory,
      pageName: (uploadDestination === UploadFileLocation.Compose) ? 'data' : 'documents',
      tenantId: authState.tenantId,
      attributes: newFileAttributes
    };
    if (uploadDestination === UploadFileLocation.Compose) {
      await DistributionService.saveAttachment(newFile)
        .then(async (resp: WSResponse) => {
          await handleResponse(resp, file, getValues);
          this.addUploadedFile(file);
          handleSuccessUpload(file.id);
        })
        .catch((err) => {
          console.error(err);
          handleFailedUpload(file.id);
        });
    } else {
      newFile._fileType = fileType;
      const page: PageMenus = UtilityService.getPageMenu(fileType);
      await DocumentService.saveDocument(file.fileName, newFile, page)
        .then(async (resp: WSResponse) => {
          await handleResponse(resp, file, getValues);
          handleSuccessUpload(file.id);
        })
        .catch((err) => {
          console.error(err);
          handleFailedUpload(file.id);
        })
    }
  }

  async callImportServlet (files: File[], listIds: string[], departmentId: string, departmentName: string): Promise<void> {
    const formData = new FormData();
    files.forEach((file: File) => {
      formData.append('file', file);
    });

    const fileId: string = UtilityService.uniqueUuid();

    NotificationUtility.addNotification(fileId, '', moment().valueOf(), NotificationType.VCard, true);
    await AuthService.authenticatedCall('/importVcard', {
      method: PostMethod,
      body: formData,
      type: 'multipart/form-data',
      params: {
        isCast: 'false',
        deptId: departmentId,
        deptName: departmentName,
        distroListIds: listIds.join(',')
      }
    }).then(() => {
      NotificationUtility.updateNotificationData({
        referenceId: fileId,
        status: NotificationStatus.Complete
      });
    }).catch((err) => {
      console.error(err);
      NotificationUtility.updateNotificationData({
        referenceId: fileId,
        status: NotificationStatus.Failed
      })
      throw new Error('import.vCard.error.server');
    })
  }

  /**
   * get the bucket name to upload file to
   */
  getBucketName (): string {
    const authState = store.getState().auth;
    return BucketId + authState.tenantId.toLowerCase();
  }

  /**
   * return a default destination for a file which is usually root
   */
  getDefaultRootDestination (fileId: string, folderId?: string): DirectoryDestination[] {
    const directory: string = folderId ?? RootFolderName;
    const bucketName: string = this.getBucketName();
    return [{
      fileURL: `${GoogleUploadUrl}${bucketName}/${fileId}/${directory}`,
      id: directory,
      parentFolder: '',
      subDirectories: [],
      title: 'Documents'
    }]
  }

  /**
   * return an array of file attachments from a distribution message uploadedFilesJSON string param
   * @param uploadedFilesJSON
   */
  getFileAttachmentsFromUploadedFilesJSON (uploadedFilesJSON: string): FileAttachment[] {
    const attachments: FileAttachment[] = [];
    const uploadedFiles = JSON.parse(uploadedFilesJSON);
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((file: any) => {
        attachments.push({
          id: file.id,
          fileName: file.fileName,
          url: file.url,
          isWatermarked: file.isWatermarked ?? '0',
          size: file.size ?? 0,
          subcategory: file.subcategory,
          isPublishedToStudio: file.isPublishedToStudio ?? false,
          cloudProvider: '',
          fileExtension: (UtilityService.getFileExtension(file.fileName) === 'pdf') ? FileType.PDF : FileType.Other,
          type: 'file'
        });
      });
    }
    return attachments;
  }
}

const handleSuccessUpload = (fileId: string): void => {
  NotificationUtility.updateUploadBox({
    fileId,
    status: UploadStatus.Complete
  });
  NotificationUtility.updateNotificationData({
    referenceId: fileId,
    status: NotificationStatus.Complete
  })
}

const handleFailedUpload = (fileId: string): void => {
  NotificationUtility.updateUploadBox({
    fileId,
    status: UploadStatus.Failed
  });
  NotificationUtility.updateNotificationData({
    referenceId: fileId,
    status: NotificationStatus.Failed
  })
}

const handleResponse = async (resp: WSResponse, file: FileAttachment, getValues: UseFormGetValues<UploadFileForm>): Promise<void> => {
  if (resp && UtilityService.isSuccessResponse(resp.responseCode)) {
    if (getValues('isParseChecked')) {
      await ScriptService.parseScript({
        fileName: file.fileName,
        description: file.fileName,
        fileId: file.id,
        episode: getValues('episode'),
        color: getValues('color'),
        version: getValues('version')
      });
    }
  } else {
    throw new Error('invalid server response');
  }
}

const uploadService: IUploadService = new UploadService();
export default uploadService;
